import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  DollarSign, TrendingUp, Package, AlertTriangle,
  Loader2, Store, ShoppingBag, Wallet,
  ArrowDownCircle, Image as ImageIcon
} from 'lucide-react';
import StatCard from './StatCard';
import TopSellersTable from './TopSellersTable';
import TopProductsTable from './TopProductsTable';
import LowStockAlerts from './LowStockAlerts';
import ImageCarouselModal from './ImageCarouselModal';
import { DeliveryPerformanceSection } from './DeliveryPerformanceSection';

function Phase1Dashboard({ userId, period, customStartDate, customEndDate }) {
  const [financialData, setFinancialData]   = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [topSellers, setTopSellers]         = useState([]);
  const [topProducts, setTopProducts]       = useState([]);
  const [lowStock, setLowStock]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchAllData();
  }, [userId, period, customStartDate, customEndDate]);

  async function fetchAllData() {
    setLoading(true);
    setError(null);
    try {
      const params = { admin_user_id: userId, period };
      if (period === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date   = customEndDate;
      }

      const [financial, performance, sellers, products, stock] = await Promise.all([
        axios.get(`${baseUrl}/api/stats/financial-overview`, { params }),
        axios.get(`${baseUrl}/api/stats/delivery-performance`, { params }),
        axios.get(`${baseUrl}/api/stats/top-sellers`,   { params: { ...params, limit: 5 } }),
        axios.get(`${baseUrl}/api/stats/top-products`,  { params: { ...params, limit: 10 } }),
        axios.get(`${baseUrl}/api/stats/low-stock`,     { params: { admin_user_id: userId, threshold: 10 } }),
      ]);

      setFinancialData(financial.data);
      setPerformanceData(performance.data);
      setTopSellers(sellers.data.sellers);
      setTopProducts(products.data.products);
      setLowStock(stock.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Error al cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-12 h-12 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-700 font-montserrat">{error}</p>
        <button
          onClick={fetchAllData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg font-montserrat hover:bg-red-700 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const hasPaymentImages = financialData?.payment_images?.length > 0;

  return (
    <div className="space-y-6">

      {/* ── Financial Overview ────────────────────────────────── */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-brand-primary" />
          Resumen Financiero
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <StatCard
            icon={DollarSign}
            label="Total Cobrado"
            value={`S/ ${financialData?.total_charged?.toFixed(2) || '0.00'}`}
            subValue={`${financialData?.billable_deliveries || 0} entregas facturables`}
            gradient="from-brand-green to-brand-gold"
          />
          <StatCard
            icon={TrendingUp}
            label="Pendiente de Cobro"
            value={`S/ ${financialData?.total_to_collect?.toFixed(2) || '0.00'}`}
            gradient="from-brand-gold to-brand-primary"
          />
          <StatCard
            icon={Package}
            label="Comisión Fast Max"
            value={`S/ ${financialData?.service_revenue?.toFixed(2) || '0.00'}`}
            gradient="from-brand-primary to-brand-gold"
          />
          <StatCard
            icon={ShoppingBag}
            label="Valor Promedio"
            value={`S/ ${financialData?.average_order_value?.toFixed(2) || '0.00'}`}
            subValue="Por entrega exitosa"
            gradient="from-brand-green to-brand-primary"
          />
        </div>

        {/* Net deposit highlight */}
        <div className="bg-gradient-to-r from-brand-primary/10 via-brand-gold/10 to-brand-green/10 rounded-xl p-6 border-2 border-brand-primary">
          <div className="flex items-center gap-4">
            <div className="relative">
              <button
                onClick={() => hasPaymentImages && setShowImageModal(true)}
                disabled={!hasPaymentImages}
                className={`w-16 h-16 rounded-xl bg-gradient-to-br from-brand-primary to-brand-gold flex items-center justify-center shadow-lg flex-shrink-0 transition-all ${
                  hasPaymentImages
                    ? 'hover:scale-110 hover:shadow-xl cursor-pointer'
                    : 'opacity-60 cursor-not-allowed'
                }`}
                title={hasPaymentImages ? 'Ver comprobantes de pago' : 'No hay comprobantes disponibles'}
              >
                <Wallet className="w-8 h-8 text-white" />
              </button>
              {hasPaymentImages && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                  <span className="text-xs text-white font-bold font-montserrat">
                    {financialData.payment_images.length}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-600 font-montserrat mb-1 flex items-center gap-2">
                <ArrowDownCircle className="w-4 h-4 text-brand-primary" />
                Monto Neto a Depositar
                {hasPaymentImages && (
                  <span className="text-xs bg-brand-gold/20 text-brand-gold px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />
                    {financialData.payment_images.length} comprobante{financialData.payment_images.length !== 1 ? 's' : ''}
                  </span>
                )}
              </p>
              <p className="text-4xl font-bold text-brand-primary font-montserrat">
                S/ {financialData?.net_to_deposit?.toFixed(2) || '0.00'}
              </p>
              <p className="text-xs text-slate-600 font-montserrat mt-1">
                Total cobrado (S/ {financialData?.total_charged?.toFixed(2) || '0.00'})
                {' - '}
                Servicio Fast Max (S/ {financialData?.service_revenue?.toFixed(2) || '0.00'})
                {financialData?.total_exchange > 0 && (
                  <> {' - '} Cambio (S/ {financialData?.total_exchange?.toFixed(2) || '0.00'})</>
                )}
              </p>
            </div>
            <div className="hidden sm:block text-right">
              <div className="bg-white rounded-lg px-4 py-2 border border-brand-primary/20">
                <p className="text-xs text-slate-600 font-montserrat mb-1">Entregas Facturables</p>
                <p className="text-2xl font-bold text-slate-900 font-montserrat">
                  {financialData?.billable_deliveries || 0}
                </p>
                <p className="text-xs text-slate-500 font-montserrat">Tarifa variable</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Delivery Performance ──────────────────────────────── */}
      <DeliveryPerformanceSection
        performanceData={performanceData}
        userId={userId}
        period={period}
        customStartDate={customStartDate}
        customEndDate={customEndDate}
      />

      {/* ── Top Sellers & Products ────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
            <Store className="w-6 h-6 text-brand-primary" />
            Top Vendedores
          </h2>
          <TopSellersTable sellers={topSellers} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-brand-primary" />
            Productos Más Vendidos
          </h2>
          <TopProductsTable products={topProducts} />
        </div>
      </div>

      {/* ── Low Stock Alerts ──────────────────────────────────── */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-brand-primary" />
          Alertas de Stock
        </h2>
        <LowStockAlerts data={lowStock} />
      </div>

      {/* Image carousel modal */}
      <ImageCarouselModal
        images={financialData?.payment_images || []}
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
      />
    </div>
  );
}

export default Phase1Dashboard;
